"use client"

/**
 * LoadingFallback
 *
 * Neutral, theme-aware, **spinner-free** loading shell used as the default
 * `loadingComponent` by:
 *   - the app-wide Firebase init gate (`AppProviders` in `@order-up/client`),
 *     i.e. the FIRST thing a user sees on a cold load, and
 *   - the route guards (`AuthenticatedRoute`, `TokenHandler`, `AuthGuard`).
 *
 * Design intent — "we're already here", not "please wait":
 * We render the *shape* of an app (a slim top bar + a content shimmer) on the
 * themed background, so first paint reads as the product loading rather than
 * an unstyled flash or a spinning circle. It is intentionally LAYOUT-NEUTRAL
 * (a generic shell, not a specific app's sidebar) so an app that hasn't wired
 * its own shell still looks intentional.
 *
 * Every app SHOULD still pass its own `loadingComponent` that mirrors its real
 * layout for a seamless boot (no skeleton-to-skeleton jump):
 *   - apps/admin       → AdminLoadingFallback
 *   - apps/management  → ManagementLoadingFallback
 *   - apps/billing     → BillingLoadingFallback
 *   - apps/staff       → StaffLoadingFallback
 *   - apps/menu        → MenuLoadingFallback
 *   - apps/onboarding  → OnboardingLoadingShell
 *
 * All colors are theme tokens (`bg-background`, `border-border`, `Skeleton`'s
 * `bg-accent`) so the shell never flashes the wrong palette in dark mode.
 */

import { Skeleton } from "../ui/skeleton"

export function LoadingFallback() {
  return (
    <div
      role="status"
      aria-label="Loading"
      aria-busy
      className="flex min-h-svh w-full flex-col bg-background"
    >
      {/* Slim top bar — reads as app chrome without committing to a layout */}
      <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border/60 px-4 sm:px-6">
        <Skeleton className="h-7 w-7 rounded-lg" />
        <Skeleton className="h-4 w-28 rounded" />
        <Skeleton className="ml-auto h-8 w-8 rounded-full" />
      </div>

      {/* Content shimmer */}
      <div className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6">
        <Skeleton className="h-7 w-48 rounded" />
        <Skeleton className="mt-3 h-4 w-72 max-w-full rounded opacity-70" />

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="space-y-3 rounded-xl border border-border/60 p-5"
            >
              <Skeleton className="h-4 w-24 rounded" />
              <Skeleton className="h-8 w-20 rounded" />
              <Skeleton className="h-3 w-full rounded opacity-70" />
            </div>
          ))}
        </div>
      </div>

      <span className="sr-only">Loading…</span>
    </div>
  )
}
